"use client";

import { createContext, useContext, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { useTalentSearchResults } from "@/components/search/talent-search-results-provider";
import { SearchCriteriaListView } from "./criteria-list";
import {
  useSearchCriteriaDrafts,
  type SearchCriteriaDraftsState,
} from "./use-criteria-drafts";

/**
 * Filter-panel "Criteria" pane, split so the list scrolls inside
 * `Filters.Body` while the Apply action stays pinned in a `Filters.Footer`.
 * Edits land in the search's unsaved draft (Save is Run), so it must render
 * inside `TalentSearchResultsProvider`:
 *
 *   <SearchCriteriaPaneProvider>
 *     <Filters.Body><SearchCriteriaPaneBody /></Filters.Body>
 *     <Filters.Footer><SearchCriteriaPaneFooter /></Filters.Footer>
 *   </SearchCriteriaPaneProvider>
 */

interface PaneContextValue {
  state: SearchCriteriaDraftsState;
  loading: boolean;
}

const PaneContext = createContext<PaneContextValue | null>(null);

function usePane(component: string) {
  const ctx = useContext(PaneContext);
  if (!ctx) {
    throw new Error(`${component} must be rendered inside SearchCriteriaPaneProvider`);
  }
  return ctx;
}

export function SearchCriteriaPaneProvider({ children }: { children: ReactNode }) {
  const { isHydrating } = useTalentSearchResults();
  const state = useSearchCriteriaDrafts();

  return (
    <PaneContext.Provider value={{ state, loading: isHydrating }}>
      {children}
    </PaneContext.Provider>
  );
}

export function SearchCriteriaPaneBody() {
  const { state, loading } = usePane("SearchCriteriaPaneBody");

  return (
    <div className="p-3">
      {loading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-16 rounded-lg bg-contrast/4 shimmer shimmer-bg text-foreground"
            />
          ))}
        </div>
      ) : (
        <SearchCriteriaListView state={state} />
      )}
    </div>
  );
}

export function SearchCriteriaPaneFooter() {
  const { state, loading } = usePane("SearchCriteriaPaneFooter");

  return (
    <Button
      type="button"
      variant="primary"
      size="sm"
      onClick={state.save}
      disabled={loading || !state.dirty}
      className="h-7 min-h-7 w-full"
    >
      Apply to draft
    </Button>
  );
}
